import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import { task, dropTask, restartableTask, type TaskInstance } from 'ember-concurrency';
import type BillingCompanyModel from 'garaje/models/billing-company';
import type CompanyModel from 'garaje/models/company';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type TenantModel from 'garaje/models/tenant';
import type TenantConnectionRequestModel from 'garaje/models/tenant-connection-request';
import type UserModel from 'garaje/models/user';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { FlowType, TenantConnectionStatus } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';
import type { Constructor } from 'type-fest';

export interface CompanyMeta {
  'employees-count': number;
  'active-locations-count': number;
}

// Flow#type comes back from the real API as `null` for Visitor flows but the FlowType enum uses '' for VISITOR_SCREENING
function flowType(flow: FlowModel): FlowType {
  return flow.type ?? FlowType.VISITOR_SCREENING;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function withUserStateFunctionality<TBase extends Constructor<any>>(Base: TBase) {
  class withUserStateFunctionality extends Base {
    declare currentUser: UserModel | null;
    declare _companyId?: string | null;

    declare currentCompany: CompanyModel | null;
    declare billingCompany: BillingCompanyModel | null;
    declare currentLocation: LocationModel | null;

    declare tenantConnections: RecordArray<TenantModel> | null;
    declare tenantConnectionRequests: RecordArray<TenantConnectionRequestModel> | null;

    declare _companyMeta: CompanyMeta | null;

    @service declare featureFlags: FeatureFlagsService;
    @service declare store: StoreService;

    initUserState = dropTask(async () => {
      this.currentUser = await this._fetchCurrentUser.perform();
    });

    _setupUserAndCompanyFeatureFlags(currentUser: UserModel, currentCompany: CompanyModel) {
      const properties = {
        ['user']: currentUser,
        ['company']: currentCompany,
      };
      // eslint-disable-next-line ember/use-ember-get-and-set
      this.featureFlags.setProperties(properties);
      return this.featureFlags.ready();
    }

    _fetchCurrentUser = restartableTask(async () => {
      return await this.store.queryRecord('user', { id: 'me' });
    });

    loadTenantConnectionRequests = dropTask(async () => {
      this.tenantConnectionRequests = await this.store.query('tenant-connection-request', {
        id: 'my-connection-requests',
        include: 'tenant,connection-request-invites',
      });
    });

    loadTenantConnections = dropTask(async () => {
      this.tenantConnections = await this.store.query('tenant', {
        filter: {
          status: TenantConnectionStatus.CONNECTED,
        },
      });
    });

    // **************************************************************************
    // Visitors specific data
    // **************************************************************************

    // Per-key cache of in-flight or completed flow loads, keyed by
    // locationId|includePropertyFlows|excludeEmployeeFlows. Replaces an
    // earlier single-slot cache that only remembered the most recent call,
    // which meant the user ping-ponging between locations re-issued
    // store.query('flow', …) every time. Concurrent responses then raced
    // in ember-data's Location.flows hasMany inverse and threw
    // "Cannot remove a resource that is not present" from Graph.update.
    _loadFlowsCache = new Map<string, TaskInstance<FlowModel[]>>();

    _flowsCacheKey(locationId: string, includePropertyFlows: boolean, excludeEmployeeFlows: boolean): string {
      return `${locationId}|${includePropertyFlows}|${excludeEmployeeFlows}`;
    }

    loadFlows({
      includePropertyFlows = false,
      locationId = null,
      reload = true,
      excludeEmployeeFlows = false,
    }: {
      includePropertyFlows?: boolean;
      locationId?: string | null;
      reload?: boolean;
      excludeEmployeeFlows?: boolean;
    } = {}) {
      const location = isPresent(locationId) ? locationId : this.currentLocation!.id;
      const cacheKey = this._flowsCacheKey(location, includePropertyFlows, excludeEmployeeFlows);
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.get, not Ember.get
      const cached = reload ? undefined : this._loadFlowsCache.get(cacheKey);

      if (cached?.isRunning) {
        // Re-use the running query for the same key.
        return cached;
      }

      if (cached?.value) {
        // Cached value present. Start from the server-filtered cached result.
        let flows = cached.value.filter((flow) => !flow.isDestroyed);

        // Merge in flows pushed into the store since the task completed, so a
        // cache-hit still surfaces a flow created since the cached query ran.
        // For a non-property query (`includePropertyFlows: false`) we exclude
        // property-owned flows, mirroring the server's `property_owned=false`
        // filter — which is defined purely as `type IN (Flows::Property,
        // Flows::PropertyWalkUp)` (envoy-web flow_resource.rb). This keeps
        // property-owned flows that an earlier `includePropertyFlows: true`
        // query loaded into the store from leaking into a non-property result,
        // while still picking up newly-created non-property flows.
        const flowIds = flows.map((flow) => flow.id);
        const flowsInStore = this.store
          .peekAll('flow')
          .filter((flow) => flow.belongsTo('location').id() === location)
          .filter((flow) => {
            if (includePropertyFlows) return true;
            // A store flow with no loaded `type` reads as VISITOR_SCREENING via
            // flowType(), so it's intentionally kept as non-property here, not
            // dropped as a gap.
            const type = flowType(flow);
            return type !== FlowType.PROPERTY && type !== FlowType.PROPERTY_WALKUP;
          });

        flowsInStore.forEach((flow) => {
          if (!flowIds.includes(flow.id)) {
            flows.push(flow);
          }
        });

        if (excludeEmployeeFlows) {
          flows = flows.filter((flow) => flowType(flow) !== FlowType.EMPLOYEE_SCREENING && !flow.employeeCentric);
        }

        // Return a promise so this function always returns a thenable.
        return Promise.resolve(flows);
      }

      // Cache miss (or explicit reload): perform a new task and store its instance.
      const instance = this._loadFlowsTask.perform(location, includePropertyFlows, excludeEmployeeFlows);
      // eslint-disable-next-line ember/use-ember-get-and-set -- Map.set, not Ember.set
      this._loadFlowsCache.set(cacheKey, instance);
      return instance;
    }

    _loadFlowsTask = task(async (locationId: string, includePropertyFlows: boolean, excludeEmployeeFlows?: boolean) => {
      const includes = ['sign-in-field-actions', 'actionable-sign-in-field-actions', 'actionable-sign-in-fields'];
      const filter: { location: string; property_owned?: string; type: string } = {
        location: locationId,
        type: 'all',
      };
      if (includePropertyFlows) {
        filter['property_owned'] = 'true,false';
      }

      const include = [
        'agreement-page',
        'active-user-document-template-configurations',
        'active-user-document-template-configurations.user-document-template',
        'flow-visit-workflow',
        'flow-questionnaires',
        'flow-questionnaires.flow',
        'flow-questionnaires.questionnaire',
      ];
      include.push(...includes.map((include) => `sign-in-field-page.sign-in-fields.${include}`));

      let flows = (await this.store.query('flow', { include: include.join(','), filter })).slice();

      // Apply client-side filters
      if (excludeEmployeeFlows) {
        flows = flows.filter((flow) => flowType(flow) !== FlowType.EMPLOYEE_SCREENING && !flow.employeeCentric);
      }

      return flows;
    });
  }

  return withUserStateFunctionality;
}

export default withUserStateFunctionality;
